import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { supabase, daysBetween, getInitials } from '../lib/supabase'
import { ArrowLeft, Save, Edit2, X } from 'lucide-react'

const AVATAR_COLORS = ['avatar-blue','avatar-green','avatar-purple','avatar-orange','avatar-pink']
const getAvatarColor = (name) => AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length]

export default function StudentDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [student, setStudent] = useState(null)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState('profile')

  useEffect(() => { fetchStudent() }, [id])

  const fetchStudent = async () => {
    setLoading(true)
    const { data } = await supabase.from('students').select('*').eq('id', id).single()
    if (data) { setStudent(data); setForm(data) }
    setLoading(false)
  }

  const save = async () => {
    setSaving(true)
    const { error } = await supabase.from('students').update(form).eq('id', id)
    if (!error) { setStudent(form); setEditing(false) }
    setSaving(false)
  }

  const toggleStatus = async () => {
    const newStatus = student.status === 'Active' ? 'Inactive' : 'Active'
    await supabase.from('students').update({ status: newStatus }).eq('id', id)
    setStudent(prev => ({ ...prev, status: newStatus }))
    setForm(prev => ({ ...prev, status: newStatus }))
  }

  if (loading) return <div className="loading">Loading student...</div>
  if (!student) return <div className="empty-state"><p>Student not found</p></div>

  const daysToMeeting = student.next_meeting_date ? daysBetween(student.next_meeting_date) : null
  const daysToRenewal = student.renewal_date ? daysBetween(student.renewal_date) : null

  const tabs = ['profile', 'meetings', 'universities', 'activities', 'ap courses', 'sat/toefl', 'transcripts', 'mbti']

  return (
    <div>
      <div className="page-header">
        <div className="header-actions">
          <Link to="/" className="back-btn"><ArrowLeft size={14}/> All students</Link>
          <div style={{width:1,height:16,background:'var(--border)',margin:'0 4px'}}/>
          <div className={`avatar ${getAvatarColor(student.english_name)}`} style={{width:28,height:28,fontSize:10}}>
            {getInitials(student.english_name)}
          </div>
          <span style={{fontSize:15,fontWeight:600}}>{student.english_name}</span>
          <span className={`badge ${student.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
            {student.status}
          </span>
        </div>
        <div className="header-actions">
          <button className="btn btn-sm" onClick={toggleStatus}>
            {student.status === 'Active' ? 'Mark inactive' : 'Mark active'}
          </button>
          {editing ? (
            <>
              <button className="btn btn-sm btn-ghost" onClick={() => { setForm(student); setEditing(false) }}>
                <X size={12}/> Cancel
              </button>
              <button className="btn btn-sm btn-primary" onClick={save} disabled={saving}>
                <Save size={12}/> {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <button className="btn btn-sm" onClick={() => setEditing(true)}>
              <Edit2 size={12}/> Edit
            </button>
          )}
        </div>
      </div>

      {/* Quick stats */}
      <div style={{padding:'12px 28px',borderBottom:'1px solid var(--border)',display:'flex',gap:20,background:'var(--white)'}}>
        <div style={{fontSize:12}}>
          <span style={{color:'var(--muted)'}}>Next meeting: </span>
          <span style={{fontWeight:500,color: daysToMeeting !== null && daysToMeeting < 0 ? 'var(--red)' : 'var(--ink)'}}>
            {daysToMeeting !== null
              ? daysToMeeting < 0 ? `${Math.abs(daysToMeeting)}d overdue` : `in ${daysToMeeting}d`
              : 'Not set'}
          </span>
        </div>
        <div style={{fontSize:12}}>
          <span style={{color:'var(--muted)'}}>Renewal: </span>
          <span style={{fontWeight:500,color: daysToRenewal !== null && daysToRenewal <= 30 ? 'var(--yellow)' : 'var(--ink)'}}>
            {daysToRenewal !== null
              ? daysToRenewal < 0 ? `${Math.abs(daysToRenewal)}d ago` : `in ${daysToRenewal}d`
              : 'Not set'}
          </span>
        </div>
        <div style={{fontSize:12}}>
          <span style={{color:'var(--muted)'}}>MBTI: </span>
          <span style={{fontWeight:500}}>{student.mbti_type || '—'}</span>
        </div>
        <div style={{fontSize:12}}>
          <span style={{color:'var(--muted)'}}>US Citizen: </span>
          <span style={{fontWeight:500}}>{student.us_citizen ? 'Yes' : 'No'}</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:0,padding:'0 28px',borderBottom:'1px solid var(--border)',background:'var(--white)'}}>
        {tabs.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding:'10px 14px',
              fontSize:12,
              fontFamily:'DM Sans,sans-serif',
              border:'none',
              background:'transparent',
              cursor:'pointer',
              color: activeTab === tab ? 'var(--accent)' : 'var(--muted)',
              borderBottom: activeTab === tab ? '2px solid var(--accent)' : '2px solid transparent',
              marginBottom:-1,
              fontWeight: activeTab === tab ? 500 : 400,
              textTransform:'capitalize',
            }}
          >{tab}</button>
        ))}
      </div>

      <div className="page-content">
        {activeTab === 'profile' && <ProfileTab student={form} editing={editing} setForm={setForm}/>}
        {activeTab === 'meetings' && <MeetingsTab studentId={id} student={student} onUpdate={fetchStudent}/>}
        {activeTab === 'universities' && <UniversitiesTab studentId={id}/>}
        {activeTab === 'activities' && <ActivitiesTab studentId={id}/>}
        {activeTab === 'ap courses' && <APTab studentId={id}/>}
        {activeTab === 'sat/toefl' && <SATTab studentId={id}/>}
        {activeTab === 'transcripts' && <TranscriptsTab studentId={id}/>}
        {activeTab === 'mbti' && <MBTITab student={form} editing={editing} setForm={setForm}/>}
      </div>
    </div>
  )
}

function ProfileTab({ student, editing, setForm }) {
  const f = (key) => editing ? (
    <input className="form-input" value={student[key] || ''} onChange={e => setForm(p => ({...p, [key]: e.target.value}))}/>
  ) : <span style={{fontSize:13}}>{student[key] || <span style={{color:'var(--muted)'}}>—</span>}</span>

  return (
    <div style={{display:'flex',flexDirection:'column',gap:20}}>
      <div className="card">
        <div className="card-header"><span className="card-title">Personal information</span></div>
        <div className="card-body">
          <div className="form-grid">
            <div className="form-group"><div className="form-label">English name</div>{f('english_name')}</div>
            <div className="form-group"><div className="form-label">Chinese name</div>{f('chinese_name')}</div>
            <div className="form-group"><div className="form-label">Date of birth</div>{f('date_of_birth')}</div>
            <div className="form-group"><div className="form-label">US Citizen</div>
              {editing ? (
                <select className="form-input" value={student.us_citizen ? 'true' : 'false'} onChange={e => setForm(p => ({...p, us_citizen: e.target.value === 'true'}))}>
                  <option value="false">No</option><option value="true">Yes</option>
                </select>
              ) : <span style={{fontSize:13}}>{student.us_citizen ? 'Yes' : 'No'}</span>}
            </div>
            <div className="form-group"><div className="form-label">Current school</div>{f('current_school')}</div>
            <div className="form-group"><div className="form-label">Grade when joined</div>{f('grade_when_joined')}</div>
            <div className="form-group"><div className="form-label">Email</div>{f('email')}</div>
            <div className="form-group"><div className="form-label">LINE ID</div>{f('line_id')}</div>
            <div className="form-group" style={{gridColumn:'1/-1'}}><div className="form-label">Home address</div>{f('home_address')}</div>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-header"><span className="card-title">Parent / Guardian</span></div>
        <div className="card-body">
          <div className="form-grid">
            <div className="form-group"><div className="form-label">Parent name</div>{f('parent_name')}</div>
            <div className="form-group"><div className="form-label">Parent contact</div>{f('parent_contact')}</div>
            <div className="form-group"><div className="form-label">Parent email</div>{f('parent_email')}</div>
            <div className="form-group"><div className="form-label">Parent LINE ID</div>{f('parent_line_id')}</div>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-header"><span className="card-title">Program</span></div>
        <div className="card-body">
          <div className="form-grid">
            <div className="form-group"><div className="form-label">Join date</div>{f('join_date')}</div>
            <div className="form-group"><div className="form-label">Renewal date</div>
              <span style={{fontSize:13}}>{student.renewal_date || '—'}</span>
            </div>
            <div className="form-group"><div className="form-label">LINE Group ID</div>{f('line_group_id')}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

function MeetingsTab({ studentId, student, onUpdate }) {
  const [meetings, setMeetings] = useState([])
  const [loading, setLoading] = useState(true)
  const [newDate, setNewDate] = useState('')
  const [newNotes, setNewNotes] = useState('')

  useEffect(() => { fetchMeetings() }, [studentId])

  const fetchMeetings = async () => {
    setLoading(true)
    const { data } = await supabase.from('meetings').select('*').eq('student_id', studentId).order('meeting_date', { ascending: false })
    if (data) setMeetings(data)
    setLoading(false)
  }

  const addMeeting = async () => {
    if (!newDate) return
    await supabase.from('meetings').insert({ student_id: studentId, meeting_date: newDate, notes: newNotes })
    await supabase.from('students').update({ last_meeting_date: newDate, meeting_booked: false, reminder_level: null }).eq('id', studentId)
    setNewDate(''); setNewNotes('')
    fetchMeetings(); onUpdate()
  }

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      <div className="card">
        <div className="card-header"><span className="card-title">Log a meeting</span></div>
        <div className="card-body">
          <div className="form-grid">
            <div className="form-group">
              <div className="form-label">Meeting date</div>
              <input type="date" className="form-input" value={newDate} onChange={e => setNewDate(e.target.value)}/>
            </div>
            <div className="form-group">
              <div className="form-label">Notes (optional)</div>
              <input className="form-input" value={newNotes} onChange={e => setNewNotes(e.target.value)} placeholder="Topics discussed..."/>
            </div>
          </div>
          <button className="btn btn-primary" style={{marginTop:12}} onClick={addMeeting}>Log meeting</button>
        </div>
      </div>
      <div className="card">
        <div className="card-header"><span className="card-title">Meeting history</span></div>
        {loading ? <div className="loading">Loading...</div> : meetings.length === 0 ? (
          <div className="empty-state"><p>No meetings logged yet</p></div>
        ) : (
          <div className="table-wrap">
            <table><thead><tr><th>Date</th><th>Notes</th></tr></thead>
              <tbody>{meetings.map(m => (
                <tr key={m.id}><td>{m.meeting_date}</td><td>{m.notes || '—'}</td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function UniversitiesTab({ studentId }) {
  const [unis, setUnis] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchUnis() }, [studentId])

  const fetchUnis = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('student_universities')
      .select('*, universities(*)')
      .eq('student_id', studentId)
      .order('tier')
    if (data) setUnis(data)
    setLoading(false)
  }

  const tiers = ['Dream', 'Target', 'Safety']
  const dotClass = { Dream: 'tier-dream', Target: 'tier-target', Safety: 'tier-safety' }

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      {loading ? <div className="loading">Loading...</div> :
        tiers.map(tier => {
          const items = unis.filter(u => u.tier === tier)
          return (
            <div key={tier} className="card">
              <div className="card-header">
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <div className={`tier-dot ${dotClass[tier]}`}/>
                  <span className="card-title">{tier} schools</span>
                  <span style={{fontSize:11,color:'var(--muted)',fontFamily:'DM Mono,monospace'}}>{items.length}/{ tier === 'Dream' ? 3 : tier === 'Target' ? 4 : 3}</span>
                </div>
              </div>
              {items.length === 0 ? (
                <div className="empty-state" style={{padding:24}}><p>No {tier.toLowerCase()} schools added yet</p></div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Rank</th><th>University</th><th>Tuition</th><th>R&B</th><th>Min GPA</th><th>Min SAT</th><th>TOEFL</th></tr></thead>
                    <tbody>{items.map(u => (
                      <tr key={u.id}>
                        <td><span style={{fontSize:11,fontFamily:'DM Mono,monospace',background:'var(--gray-l)',padding:'2px 6px',borderRadius:4}}>#{u.universities?.rank}</span></td>
                        <td><div style={{fontWeight:600,fontSize:13}}>{u.universities?.name}</div><div style={{fontSize:11,color:'var(--muted)'}}>{u.universities?.location}</div></td>
                        <td>${u.universities?.tuition?.toLocaleString()}</td>
                        <td>${u.universities?.room_and_board?.toLocaleString()}</td>
                        <td>{u.universities?.min_gpa}</td>
                        <td>{u.universities?.min_sat}</td>
                        <td>{u.universities?.min_toefl || <span style={{color:'var(--muted)'}}>—</span>}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          )
        })
      }
    </div>
  )
}

function ActivitiesTab({ studentId }) {
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ name:'', category:'Extracurricular', status:'In Progress', grade_start:'', description:'' })

  useEffect(() => { fetchActivities() }, [studentId])
  const fetchActivities = async () => {
    setLoading(true)
    const { data } = await supabase.from('activities').select('*').eq('student_id', studentId).order('grade_start')
    if (data) setActivities(data)
    setLoading(false)
  }

  const addActivity = async () => {
    await supabase.from('activities').insert({ ...form, student_id: studentId })
    setAdding(false)
    setForm({ name:'', category:'Extracurricular', status:'In Progress', grade_start:'', description:'' })
    fetchActivities()
  }

  const categories = ['Extracurricular','Volunteering','School Club','Summer Program','Specialization','Competition','Internship','Self Project','Other']
  const statusColor = { 'Planned': 'badge-gray', 'In Progress': 'badge-blue', 'Completed': 'badge-green' }

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      <div className="section-header">
        <span className="section-title">{activities.length} activities</span>
        <button className="btn btn-sm btn-primary" onClick={() => setAdding(!adding)}>+ Add activity</button>
      </div>
      {adding && (
        <div className="card">
          <div className="card-header"><span className="card-title">New activity</span></div>
          <div className="card-body" style={{display:'flex',flexDirection:'column',gap:12}}>
            <div className="form-grid">
              <div className="form-group"><div className="form-label">Name</div>
                <input className="form-input" value={form.name} onChange={e => setForm(p=>({...p,name:e.target.value}))} placeholder="Activity name"/>
              </div>
              <div className="form-group"><div className="form-label">Category</div>
                <select className="form-input" value={form.category} onChange={e => setForm(p=>({...p,category:e.target.value}))}>
                  {categories.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group"><div className="form-label">Grade start</div>
                <input className="form-input" type="number" min="8" max="12" value={form.grade_start} onChange={e => setForm(p=>({...p,grade_start:e.target.value}))}/>
              </div>
              <div className="form-group"><div className="form-label">Status</div>
                <select className="form-input" value={form.status} onChange={e => setForm(p=>({...p,status:e.target.value}))}>
                  <option>Planned</option><option>In Progress</option><option>Completed</option>
                </select>
              </div>
              <div className="form-group" style={{gridColumn:'1/-1'}}><div className="form-label">Description</div>
                <input className="form-input" value={form.description} onChange={e => setForm(p=>({...p,description:e.target.value}))} placeholder="Brief description..."/>
              </div>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-primary btn-sm" onClick={addActivity}>Save</button>
              <button className="btn btn-sm" onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      <div className="card">
        {loading ? <div className="loading">Loading...</div> : activities.length === 0 ? (
          <div className="empty-state"><p>No activities added yet</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Name</th><th>Category</th><th>Grade</th><th>Status</th><th>Certificate</th></tr></thead>
              <tbody>{activities.map(a => (
                <tr key={a.id}>
                  <td><div style={{fontWeight:500}}>{a.name}</div><div style={{fontSize:11,color:'var(--muted)'}}>{a.description}</div></td>
                  <td><span className="badge badge-blue">{a.category}</span></td>
                  <td>{a.grade_start}{a.grade_end && a.grade_end !== a.grade_start ? `–${a.grade_end}` : ''}</td>
                  <td><span className={`badge ${statusColor[a.status]}`}>{a.status}</span></td>
                  <td>{a.has_certificate ? <span className="badge badge-green">✓</span> : <span className="badge badge-gray">—</span>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function APTab({ studentId }) {
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ course_name:'', grade_taken:'', location:'In school', status:'Planned', score:'' })

  useEffect(() => { fetchCourses() }, [studentId])
  const fetchCourses = async () => {
    setLoading(true)
    const { data } = await supabase.from('ap_courses').select('*').eq('student_id', studentId).order('grade_taken')
    if (data) setCourses(data)
    setLoading(false)
  }

  const addCourse = async () => {
    await supabase.from('ap_courses').insert({ ...form, student_id: studentId, score: form.score || null })
    setAdding(false); fetchCourses()
  }

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      <div className="section-header">
        <span className="section-title">{courses.length} AP courses</span>
        <button className="btn btn-sm btn-primary" onClick={() => setAdding(!adding)}>+ Add course</button>
      </div>
      {adding && (
        <div className="card">
          <div className="card-body" style={{display:'flex',flexDirection:'column',gap:12}}>
            <div className="form-grid-3">
              <div className="form-group"><div className="form-label">Course name</div>
                <input className="form-input" value={form.course_name} onChange={e => setForm(p=>({...p,course_name:e.target.value}))} placeholder="e.g. AP Calculus BC"/>
              </div>
              <div className="form-group"><div className="form-label">Grade</div>
                <input className="form-input" type="number" min="8" max="12" value={form.grade_taken} onChange={e => setForm(p=>({...p,grade_taken:e.target.value}))}/>
              </div>
              <div className="form-group"><div className="form-label">Score (1-5)</div>
                <input className="form-input" type="number" min="1" max="5" value={form.score} onChange={e => setForm(p=>({...p,score:e.target.value}))} placeholder="Leave blank if not taken"/>
              </div>
              <div className="form-group"><div className="form-label">Location</div>
                <select className="form-input" value={form.location} onChange={e => setForm(p=>({...p,location:e.target.value}))}>
                  <option>In school</option><option>Outside school</option>
                </select>
              </div>
              <div className="form-group"><div className="form-label">Status</div>
                <select className="form-input" value={form.status} onChange={e => setForm(p=>({...p,status:e.target.value}))}>
                  <option>Planned</option><option>In Progress</option><option>Completed</option>
                </select>
              </div>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-primary btn-sm" onClick={addCourse}>Save</button>
              <button className="btn btn-sm" onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      <div className="card">
        {loading ? <div className="loading">Loading...</div> : courses.length === 0 ? (
          <div className="empty-state"><p>No AP courses added yet</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Course</th><th>Grade</th><th>Location</th><th>Status</th><th>Score</th></tr></thead>
              <tbody>{courses.map(c => (
                <tr key={c.id}>
                  <td style={{fontWeight:500}}>{c.course_name}</td>
                  <td>Grade {c.grade_taken}</td>
                  <td>{c.location}</td>
                  <td><span className={`badge ${c.status==='Completed'?'badge-green':c.status==='In Progress'?'badge-blue':'badge-gray'}`}>{c.status}</span></td>
                  <td>{c.score ? <strong>{c.score}/5</strong> : <span style={{color:'var(--muted)'}}>—</span>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function SATTab({ studentId }) {
  const [satScores, setSatScores] = useState([])
  const [toeflScores, setToeflScores] = useState([])
  const [loading, setLoading] = useState(true)
  const [addingSAT, setAddingSAT] = useState(false)
  const [addingTOEFL, setAddingTOEFL] = useState(false)
  const [satForm, setSatForm] = useState({ test_date:'', reading_writing:'', math:'', total:'' })
  const [toeflForm, setToeflForm] = useState({ test_date:'', reading:'', listening:'', speaking:'', writing:'', total:'' })

  useEffect(() => { fetchScores() }, [studentId])
  const fetchScores = async () => {
    setLoading(true)
    const [sat, toefl] = await Promise.all([
      supabase.from('sat_scores').select('*').eq('student_id', studentId).order('test_date', {ascending:false}),
      supabase.from('toefl_scores').select('*').eq('student_id', studentId).order('test_date', {ascending:false}),
    ])
    if (sat.data) setSatScores(sat.data)
    if (toefl.data) setToeflScores(toefl.data)
    setLoading(false)
  }

  const addSAT = async () => {
    const rw = parseInt(satForm.reading_writing)||0, m = parseInt(satForm.math)||0
    await supabase.from('sat_scores').insert({ ...satForm, student_id: studentId, reading_writing: rw, math: m, total: rw+m })
    setAddingSAT(false); fetchScores()
  }

  const addTOEFL = async () => {
    const r=parseInt(toeflForm.reading)||0, l=parseInt(toeflForm.listening)||0, s=parseInt(toeflForm.speaking)||0, w=parseInt(toeflForm.writing)||0
    await supabase.from('toefl_scores').insert({ ...toeflForm, student_id: studentId, reading:r, listening:l, speaking:s, writing:w, total:r+l+s+w })
    setAddingTOEFL(false); fetchScores()
  }

  const bestSAT = satScores.length > 0 ? Math.max(...satScores.map(s => s.total)) : null
  const bestTOEFL = toeflScores.length > 0 ? Math.max(...toeflScores.map(s => s.total)) : null

  return (
    <div style={{display:'flex',flexDirection:'column',gap:20}}>
      {/* SAT */}
      <div className="card">
        <div className="card-header">
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <span className="card-title">SAT scores</span>
            {bestSAT && <span className="badge badge-blue">Best: {bestSAT}</span>}
          </div>
          <button className="btn btn-sm btn-primary" onClick={() => setAddingSAT(!addingSAT)}>+ Add attempt</button>
        </div>
        {addingSAT && (
          <div style={{padding:16,borderBottom:'1px solid var(--border)'}}>
            <div className="form-grid-3" style={{marginBottom:10}}>
              <div className="form-group"><div className="form-label">Test date</div>
                <input type="date" className="form-input" value={satForm.test_date} onChange={e => setSatForm(p=>({...p,test_date:e.target.value}))}/>
              </div>
              <div className="form-group"><div className="form-label">Reading & Writing</div>
                <input className="form-input" type="number" value={satForm.reading_writing} onChange={e => setSatForm(p=>({...p,reading_writing:e.target.value}))} placeholder="200–800"/>
              </div>
              <div className="form-group"><div className="form-label">Math</div>
                <input className="form-input" type="number" value={satForm.math} onChange={e => setSatForm(p=>({...p,math:e.target.value}))} placeholder="200–800"/>
              </div>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-primary btn-sm" onClick={addSAT}>Save</button>
              <button className="btn btn-sm" onClick={() => setAddingSAT(false)}>Cancel</button>
            </div>
          </div>
        )}
        {loading ? <div className="loading">Loading...</div> : satScores.length === 0 ? (
          <div className="empty-state" style={{padding:24}}><p>No SAT scores recorded</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Date</th><th>Reading & Writing</th><th>Math</th><th>Total</th></tr></thead>
              <tbody>{satScores.map(s => (
                <tr key={s.id}>
                  <td>{s.test_date}</td>
                  <td>{s.reading_writing}</td>
                  <td>{s.math}</td>
                  <td><strong style={{fontSize:14}}>{s.total}</strong>{s.total === bestSAT && <span className="badge badge-green" style={{marginLeft:6}}>Best</span>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>

      {/* TOEFL */}
      <div className="card">
        <div className="card-header">
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <span className="card-title">TOEFL scores</span>
            {bestTOEFL && <span className="badge badge-blue">Best: {bestTOEFL}</span>}
          </div>
          <button className="btn btn-sm btn-primary" onClick={() => setAddingTOEFL(!addingTOEFL)}>+ Add attempt</button>
        </div>
        {addingTOEFL && (
          <div style={{padding:16,borderBottom:'1px solid var(--border)'}}>
            <div className="form-grid-3" style={{marginBottom:10}}>
              <div className="form-group"><div className="form-label">Test date</div>
                <input type="date" className="form-input" value={toeflForm.test_date} onChange={e => setToeflForm(p=>({...p,test_date:e.target.value}))}/>
              </div>
              <div className="form-group"><div className="form-label">Reading</div>
                <input className="form-input" type="number" value={toeflForm.reading} onChange={e => setToeflForm(p=>({...p,reading:e.target.value}))} placeholder="0–30"/>
              </div>
              <div className="form-group"><div className="form-label">Listening</div>
                <input className="form-input" type="number" value={toeflForm.listening} onChange={e => setToeflForm(p=>({...p,listening:e.target.value}))} placeholder="0–30"/>
              </div>
              <div className="form-group"><div className="form-label">Speaking</div>
                <input className="form-input" type="number" value={toeflForm.speaking} onChange={e => setToeflForm(p=>({...p,speaking:e.target.value}))} placeholder="0–30"/>
              </div>
              <div className="form-group"><div className="form-label">Writing</div>
                <input className="form-input" type="number" value={toeflForm.writing} onChange={e => setToeflForm(p=>({...p,writing:e.target.value}))} placeholder="0–30"/>
              </div>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-primary btn-sm" onClick={addTOEFL}>Save</button>
              <button className="btn btn-sm" onClick={() => setAddingTOEFL(false)}>Cancel</button>
            </div>
          </div>
        )}
        {loading ? <div className="loading">Loading...</div> : toeflScores.length === 0 ? (
          <div className="empty-state" style={{padding:24}}><p>No TOEFL scores recorded</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Date</th><th>Reading</th><th>Listening</th><th>Speaking</th><th>Writing</th><th>Total</th></tr></thead>
              <tbody>{toeflScores.map(s => (
                <tr key={s.id}>
                  <td>{s.test_date}</td><td>{s.reading}</td><td>{s.listening}</td><td>{s.speaking}</td><td>{s.writing}</td>
                  <td><strong style={{fontSize:14}}>{s.total}</strong>{s.total === bestTOEFL && <span className="badge badge-green" style={{marginLeft:6}}>Best</span>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function TranscriptsTab({ studentId }) {
  const [transcripts, setTranscripts] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ school_year:'', grade:'', gpa:'', transcript_url:'' })

  useEffect(() => { fetchTranscripts() }, [studentId])
  const fetchTranscripts = async () => {
    setLoading(true)
    const { data } = await supabase.from('transcripts').select('*').eq('student_id', studentId).order('grade')
    if (data) setTranscripts(data)
    setLoading(false)
  }

  const addTranscript = async () => {
    await supabase.from('transcripts').insert({ ...form, student_id: studentId })
    setAdding(false); fetchTranscripts()
  }

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      <div className="section-header">
        <span className="section-title">GPA records</span>
        <button className="btn btn-sm btn-primary" onClick={() => setAdding(!adding)}>+ Add year</button>
      </div>
      {adding && (
        <div className="card">
          <div className="card-body" style={{display:'flex',flexDirection:'column',gap:12}}>
            <div className="form-grid">
              <div className="form-group"><div className="form-label">School year</div>
                <input className="form-input" value={form.school_year} onChange={e => setForm(p=>({...p,school_year:e.target.value}))} placeholder="e.g. 2024-2025"/>
              </div>
              <div className="form-group"><div className="form-label">Grade</div>
                <input className="form-input" type="number" min="8" max="12" value={form.grade} onChange={e => setForm(p=>({...p,grade:e.target.value}))}/>
              </div>
              <div className="form-group"><div className="form-label">GPA</div>
                <input className="form-input" type="number" step="0.001" min="0" max="4" value={form.gpa} onChange={e => setForm(p=>({...p,gpa:e.target.value}))} placeholder="e.g. 3.850"/>
              </div>
              <div className="form-group"><div className="form-label">Transcript URL (Google Drive)</div>
                <input className="form-input" value={form.transcript_url} onChange={e => setForm(p=>({...p,transcript_url:e.target.value}))} placeholder="https://drive.google.com/..."/>
              </div>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-primary btn-sm" onClick={addTranscript}>Save</button>
              <button className="btn btn-sm" onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      <div className="card">
        {loading ? <div className="loading">Loading...</div> : transcripts.length === 0 ? (
          <div className="empty-state"><p>No transcripts added yet</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>School year</th><th>Grade</th><th>GPA</th><th>Transcript</th></tr></thead>
              <tbody>{transcripts.map(t => (
                <tr key={t.id}>
                  <td>{t.school_year}</td>
                  <td>Grade {t.grade}</td>
                  <td><strong>{t.gpa}</strong></td>
                  <td>{t.transcript_url ? <a href={t.transcript_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-ghost">View ↗</a> : <span style={{color:'var(--muted)'}}>—</span>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function MBTITab({ student, editing, setForm }) {
  const types = ['INTJ','INTP','ENTJ','ENTP','INFJ','INFP','ENFJ','ENFP','ISTJ','ISFJ','ESTJ','ESFJ','ISTP','ISFP','ESTP','ESFP']
  const typeDescriptions = {
    INTJ:'Architect','INTP':'Logician','ENTJ':'Commander','ENTP':'Debater',
    INFJ:'Advocate','INFP':'Mediator','ENFJ':'Protagonist','ENFP':'Campaigner',
    ISTJ:'Logistician','ISFJ':'Defender','ESTJ':'Executive','ESFJ':'Consul',
    ISTP:'Virtuoso','ISFP':'Adventurer','ESTP':'Entrepreneur','ESFP':'Entertainer'
  }

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      <div className="card">
        <div className="card-header"><span className="card-title">Myers-Briggs result</span></div>
        <div className="card-body">
          <div className="form-grid">
            <div className="form-group">
              <div className="form-label">Personality type</div>
              {editing ? (
                <select className="form-input" value={student.mbti_type || ''} onChange={e => setForm(p=>({...p,mbti_type:e.target.value}))}>
                  <option value="">Not completed</option>
                  {types.map(t => <option key={t} value={t}>{t} — {typeDescriptions[t]}</option>)}
                </select>
              ) : (
                <div style={{display:'flex',alignItems:'center',gap:10,marginTop:4}}>
                  {student.mbti_type ? (
                    <>
                      <span style={{fontSize:28,fontFamily:'Instrument Serif,serif',color:'var(--accent)'}}>{student.mbti_type}</span>
                      <span style={{fontSize:14,color:'var(--muted)'}}>{typeDescriptions[student.mbti_type]}</span>
                    </>
                  ) : <span style={{color:'var(--muted)',fontSize:13}}>Not completed yet</span>}
                </div>
              )}
            </div>
            <div className="form-group">
              <div className="form-label">Result URL</div>
              {editing ? (
                <input className="form-input" value={student.mbti_result_url || ''} onChange={e => setForm(p=>({...p,mbti_result_url:e.target.value}))} placeholder="https://www.16personalities.com/..."/>
              ) : student.mbti_result_url ? (
                <a href={student.mbti_result_url} target="_blank" rel="noreferrer" className="btn btn-sm" style={{marginTop:4}}>View full result ↗</a>
              ) : <span style={{color:'var(--muted)',fontSize:13}}>—</span>}
            </div>
          </div>
          {!student.mbti_type && (
            <div style={{marginTop:16,padding:14,background:'var(--accent-l)',borderRadius:'var(--radius-sm)',fontSize:13,color:'var(--accent)'}}>
              Share this link with the student: <a href="https://www.16personalities.com/free-personality-test" target="_blank" rel="noreferrer" style={{fontWeight:600}}>16personalities.com/free-personality-test</a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
